import React, { useEffect, useMemo, useState } from "react"
import {
  Modal,
  Form,
  Select,
  Button,
  DatePicker,
  InputNumber,
  Spin,
  Tooltip,
  message,
} from "antd"
import dayjs from "dayjs"
import { translations } from "../../utils/Translation/staticTranslation"
import { PlansApi } from "../../Apis/plansApi"
import { DashboardUsersApi } from "../../Apis/dashboardUsersApi"
import { Analytics } from "../../Apis/analyticsApi"
import {
  parseAdminPlansListResponse,
  filterPlansForDashboardUserCreate,
  mapPlansToSelectOptions,
  pickLocalizedField,
} from "../../utils/plansPricingNormalize"
import { getStoreApiErrorMessage } from "../../helper/storeApiErrorMessage"
import "../../styles/Pages/create-roles.css"

function hasActivePlan(plan) {
  const value = String(plan ?? "").trim()
  if (!value || value === "—") return false

  const lower = value.toLowerCase()
  if (lower === "none" || lower === "no plan" || lower === "no plans") {
    return false
  }

  if (value.includes("لا توجد باقة") || value.includes("بدون باقة")) {
    return false
  }

  return true
}

function parseUserDetailResponse(res) {
  return res?.data?.results?.[0] || res?.results?.[0] || res?.data || res
}

function resolveRemainingImages(detail) {
  if (detail?.remaining_images != null) return Number(detail.remaining_images)
  const total = Number(detail?.total_credits)
  const used = Number(detail?.credit_usage)
  if (Number.isFinite(total) && Number.isFinite(used)) {
    return Math.max(0, total - used)
  }
  return null
}

function resolveRemainingFurnitureFinder(detail) {
  if (detail?.remaining_furniture_finder != null) {
    return Number(detail.remaining_furniture_finder)
  }
  if (detail?.furniture_finder?.remaining != null) {
    return Number(detail.furniture_finder.remaining)
  }
  return null
}

function normalizePlanName(value) {
  const normalized = String(value ?? "").trim().toLowerCase()
  if (!normalized || normalized === "none" || normalized === "—") return ""
  return normalized
}

function getActiveSubscription(detail) {
  const subs = detail?.subscription_history?.results
  if (!Array.isArray(subs) || subs.length === 0) return null
  return subs.find((sub) => sub.is_active) || subs[0]
}

function collectPlanNameCandidates(detail, userRecord) {
  const activeSub = getActiveSubscription(detail)
  return [
    detail?.plan_name,
    detail?.arabic_plan_name,
    detail?.active_plan,
    detail?.plan_arabic,
    userRecord?.plan,
    userRecord?.planEn,
    userRecord?.planAr,
    activeSub?.plan_name,
    activeSub?.plan_name_arabic,
  ]
    .map(normalizePlanName)
    .filter(Boolean)
}

function findPlanOptionByName(planOptions, names) {
  const candidates = [...new Set(names.map(normalizePlanName).filter(Boolean))]
  if (candidates.length === 0) return null

  return (
    planOptions.find((opt) => {
      const label = normalizePlanName(opt.label)
      return candidates.some(
        (name) =>
          label === name || label.includes(name) || name.includes(label)
      )
    }) || null
  )
}

function findPlanIdFromRawPlans(plans, names) {
  const candidates = [...new Set(names.map(normalizePlanName).filter(Boolean))]
  if (candidates.length === 0) return null

  for (const plan of plans) {
    const labels = [
      plan.name,
      plan.name_en,
      plan.name_english,
      plan.name_ar,
      plan.name_arabic,
      pickLocalizedField(plan, "en", "name_en", "name_ar", "name"),
      pickLocalizedField(plan, "ar", "name_en", "name_ar", "name"),
    ]
      .map(normalizePlanName)
      .filter(Boolean)

    const matches = candidates.some((candidate) =>
      labels.some(
        (label) =>
          label === candidate ||
          label.includes(candidate) ||
          candidate.includes(label)
      )
    )

    if (matches) {
      const id = Number(plan.id ?? plan.pk)
      if (Number.isFinite(id)) return id
    }
  }

  return null
}

function resolvePlanId(detail, planOptions, userRecord, rawPlans = []) {
  const activeSub = getActiveSubscription(detail)
  const idCandidates = [
    userRecord?.planId,
    detail?.plan_id,
    detail?.active_plan_id,
    detail?.current_plan_id,
    activeSub?.plan_id,
  ]

  for (const rawId of idCandidates) {
    if (rawId == null || rawId === "") continue
    const id = Number(rawId)
    if (Number.isFinite(id)) return id
  }

  const nameCandidates = collectPlanNameCandidates(detail, userRecord)

  const match = findPlanOptionByName(planOptions, nameCandidates)
  if (match?.value != null) return match.value

  return findPlanIdFromRawPlans(rawPlans, nameCandidates)
}

function resolvePlanLabel(detail, userRecord, lang, planId, planOptions) {
  const fromOption = planOptions.find((opt) => opt.value === planId)?.label
  if (fromOption) return fromOption

  const activeSub = getActiveSubscription(detail)
  const localized =
    lang === "ar"
      ? detail?.arabic_plan_name ||
        detail?.plan_arabic ||
        userRecord?.planAr ||
        activeSub?.plan_name_arabic ||
        userRecord?.plan
      : detail?.plan_name ||
        detail?.active_plan ||
        userRecord?.planEn ||
        activeSub?.plan_name ||
        userRecord?.plan

  return localized && localized !== "None" ? localized : null
}

function withCurrentPlanOption(options, planId, planLabel) {
  if (planId == null || planId === "") return options
  const id = Number(planId)
  if (!Number.isFinite(id)) return options
  if (options.some((opt) => opt.value === id)) return options
  if (!planLabel) return options
  return [{ value: id, label: planLabel }, ...options]
}

const UpdateDashboardUserPlanModal = ({
  open,
  onClose,
  onSuccess,
  userId,
  userRecord,
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.allUsersPage || {}
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [plansLoading, setPlansLoading] = useState(false)
  const [planOptions, setPlanOptions] = useState([])
  const [pendingFormValues, setPendingFormValues] = useState(null)
  const [activeTab, setActiveTab] = useState("plan")
  const [userDetail, setUserDetail] = useState(null)

  const planLabel =
    lang === "ar"
      ? userDetail?.arabic_plan_name || userRecord?.plan
      : userDetail?.plan_name || userRecord?.plan

  const userHasActivePlan = useMemo(() => {
    if (userDetail?.is_active === false) return false
    return hasActivePlan(planLabel)
  }, [planLabel, userDetail?.is_active])

  const remainingImages = useMemo(
    () => resolveRemainingImages(userDetail),
    [userDetail]
  )

  const remainingFurnitureFinder = useMemo(
    () => resolveRemainingFurnitureFinder(userDetail),
    [userDetail]
  )
  const currentPlanDisplay = planLabel && planLabel !== "None" ? planLabel : "—"

  useEffect(() => {
    if (!open || !userId) return

    let cancelled = false
    setActiveTab("plan")
    setUserDetail(null)
    setPendingFormValues(null)
    form.resetFields()

    const load = async () => {
      setLoadingDetail(true)
      setPlansLoading(true)
      try {
        const [detailRes, plansRes] = await Promise.all([
          Analytics.getUserDetail(userId),
          PlansApi.getPlansList({
            status: "all",
            planCategory: "standard",
            page: 1,
            pageSize: 200,
          }),
        ])
        if (cancelled) return

        const detail = parseUserDetailResponse(detailRes)
        const allPlans = parseAdminPlansListResponse(plansRes?.data)
        const assignablePlans = filterPlansForDashboardUserCreate(allPlans)
        const allPlanOptions = mapPlansToSelectOptions(allPlans, lang)
        const baseOptions = mapPlansToSelectOptions(assignablePlans, lang)

        const planId = resolvePlanId(detail, allPlanOptions, userRecord, allPlans)
        const planLabel = resolvePlanLabel(
          detail,
          userRecord,
          lang,
          planId,
          allPlanOptions
        )
        const options = withCurrentPlanOption(baseOptions, planId, planLabel)

        setUserDetail(detail)
        setPlanOptions(options)
        setPendingFormValues({
          plan_id: undefined,
          account_access_expires_at: detail?.account_access_expires_at
            ? dayjs(detail.account_access_expires_at)
            : undefined,
          current_images: undefined,
          furniture_finder: undefined,
        })
      } catch {
        if (!cancelled) {
          message.error(
            t.error_user_detail_load || "Could not load user details"
          )
        }
      } finally {
        if (!cancelled) {
          setLoadingDetail(false)
          setPlansLoading(false)
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [open, userId, userRecord, form, lang, t.error_user_detail_load])

  useEffect(() => {
    if (!open || !pendingFormValues) return
    if (pendingFormValues.plan_id != null && planOptions.length === 0) return
    form.setFieldsValue(pendingFormValues)
    setPendingFormValues(null)
  }, [open, pendingFormValues, planOptions, form])

  const handleTabChange = (tab) => {
    if (tab === "images" && !userHasActivePlan) return
    setActiveTab(tab)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (!userId) return

      const payload = {}
      if (values.plan_id != null && values.plan_id !== "") {
        payload.plan_id = values.plan_id
      }
      if (values.current_images != null && values.current_images !== "") {
        payload.current_images = Number(values.current_images)
      }
      if (values.furniture_finder != null && values.furniture_finder !== "") {
        payload.furniture_finder = Number(values.furniture_finder)
      }
      if (values.account_access_expires_at) {
        payload.account_access_expires_at = dayjs(values.account_access_expires_at)
          .endOf("day")
          .toISOString()
      }

      if (Object.keys(payload).length === 0) {
        message.warning(
          t.update_validation_required ||
            "Please update at least one field"
        )
        return
      }

      setSubmitting(true)
      await DashboardUsersApi.update(userId, payload)
      message.success(
        t.update_success || "Plan updated successfully"
      )
      onClose()
      onSuccess?.()
    } catch (error) {
      if (error?.errorFields) return
      message.error(
        getStoreApiErrorMessage(error?.response?.data, lang, {
          generic: t.update_error || "Could not update plan",
        })
      )
    } finally {
      setSubmitting(false)
    }
  }

  const isRTL = lang === "ar"

  const assignImagesTab = (
    <button
      type="button"
      className={`update-plan-tab ${activeTab === "images" ? "active" : ""} ${
        !userHasActivePlan ? "disabled" : ""
      }`}
      onClick={() => handleTabChange("images")}
      disabled={!userHasActivePlan}
    >
      {t.tab_assign_images || "Assign Images"}
    </button>
  )

  return (
    <Modal
      title={
        <div className="create-user-modal-title-wrap">
          <div className="create-user-modal-title-main">
            {t.update_modal_title || "Update Plan"}
          </div>
          {userRecord?.name ? (
            <div className="create-user-modal-title-sub">{userRecord.name}</div>
          ) : null}
        </div>
      }
      open={open}
      onCancel={() => {
        if (!submitting) onClose()
      }}
      width={560}
      centered
      destroyOnClose
      footer={null}
      className="create-user-modal create-dashboard-user-modal update-plan-modal"
      maskClosable={false}
    >
      <div className="create-user-modal-inner" dir={isRTL ? "rtl" : "ltr"}>
        <div className="update-plan-tabs">
          <button
            type="button"
            className={`update-plan-tab ${activeTab === "plan" ? "active" : ""}`}
            onClick={() => handleTabChange("plan")}
          >
            {t.tab_assign_plan || "Assign Plan"}
          </button>
          {!userHasActivePlan ? (
            <Tooltip
              title={
                t.assign_images_disabled_hint ||
                "No active subscription. Assign plan first."
              }
            >
              <span className="update-plan-tab-tooltip-wrap">
                {assignImagesTab}
              </span>
            </Tooltip>
          ) : (
            assignImagesTab
          )}
        </div>

        <Spin spinning={loadingDetail}>
          <Form
            form={form}
            layout="vertical"
            autoComplete="off"
            className="create-dashboard-user-modal-form"
          >
            {activeTab === "plan" ? (
              <>
                <div className="update-plan-remaining-stats">
                  <div className="update-plan-remaining-stat">
                    <span className="update-plan-remaining-label">
                      {t.current_plan || "Current Plan"}
                    </span>
                    <strong>{currentPlanDisplay}</strong>
                  </div>
                  <div className="update-plan-remaining-stat">
                    <span className="update-plan-remaining-label">
                      {t.remaining_images || "Remaining Images"}
                    </span>
                    <strong>{remainingImages != null ? remainingImages : "—"}</strong>
                  </div>
                  <div className="update-plan-remaining-stat">
                    <span className="update-plan-remaining-label">
                      {t.furniture_finder || "Furniture Finder"}
                    </span>
                    <strong>
                      {remainingFurnitureFinder != null
                        ? remainingFurnitureFinder
                        : "—"}
                    </strong>
                  </div>
                </div>

                <Form.Item name="plan_id" label={t.plan || "Plan"}>
                  <Select
                    className="create-user-modal-select"
                    size="large"
                    placeholder={t.plan_placeholder || "Select a plan"}
                    options={planOptions}
                    loading={plansLoading}
                    disabled={plansLoading && planOptions.length === 0}
                    allowClear
                  />
                </Form.Item>

                <Form.Item
                  name="account_access_expires_at"
                  label={t.expiry || "Access expires"}
                >
                  <DatePicker
                    className="create-user-modal-input"
                    size="large"
                    style={{ width: "100%" }}
                    placeholder={t.expiry_placeholder}
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item
                  name="current_images"
                  label={t.images || "Images"}
                  extra={t.images_hint}
                >
                  <InputNumber
                    min={0}
                    className="w-100 create-user-modal-input"
                    style={{ width: "100%" }}
                    placeholder={t.images_placeholder || "e.g. 120"}
                  />
                </Form.Item>

                <Form.Item
                  name="furniture_finder"
                  label={t.furniture_finder || "Furniture Finder"}
                  extra={t.furniture_finder_hint}
                >
                  <InputNumber
                    min={0}
                    className="w-100 create-user-modal-input"
                    style={{ width: "100%" }}
                    placeholder={t.furniture_finder_placeholder || "e.g. 30"}
                  />
                </Form.Item>
              </>
            )}
          </Form>
        </Spin>

        <div className="create-user-modal-footer create-dashboard-user-modal-footer">
          <Button
            type="default"
            className="create-user-modal-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            {t.cancel || "Cancel"}
          </Button>
          <Button
            className="create-roles-action-btn create-user-modal-submit"
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting || loadingDetail}
          >
            {t.update_submit || "Update"}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default UpdateDashboardUserPlanModal
